"use client";

import { useMemo, useState, type ReactNode } from "react";
import { useScheduler } from "../SchedulerContext";
import { useStepNavigation } from "../StepNavigationContext";
import { useStepAccess } from "../utils/useStepAccess";
import { useStepValidation } from "../utils/useStepValidation";
import { useTZDayjs } from "@/context/TimezoneContext";
import { Avatar, Button, Tooltip } from "@heroui/react";
import {
  FaScrewdriverWrench,
  FaCalendarDay,
  FaChevronUp,
  FaChevronDown,
  FaUser,
  FaHouse,
} from "react-icons/fa6";
import { Service, Bundle, ServWithMods } from "@/util/types/serverTypes";
import { useCountdown } from "../hooks/useCountdown";
import IconDisplay from "@/components/ui/IconDisplay";
import { checkEscapeHatch } from "../utils/checkEscapeHatch";
import { jobDurationHoursForScheduling } from "@/util/functions/schedulingHelpers/serviceDurationHelpers";

type SchedulerSummaryBarProps = {
  services: ServWithMods[];
  bundles: Bundle[];
};

const SECTION_CLASSES =
  "flex min-h-[70px] min-w-0 cursor-pointer flex-col justify-center rounded-lg border p-3 transition-colors hover:border-gray-400 hover:bg-white/50";

// Helper to find service in services array
const findService = (serviceId: string, services: Service[]): Service | undefined => {
  return services.find((s) => s._id === serviceId);
};

/**
 * This bar is pinned to every step, so its tiles were the easiest way
 * to jump over an unfinished step (a click straight from Property to Schedule
 * skipped the More Information questions). Both wrappers below refuse to
 * navigate while the target step is blocked and explain why on hover — the same
 * treatment the escape hatch already gave the slot tile.
 *
 * Declared at module scope, not inline, so the tiles are not torn down and
 * rebuilt on every render of the bar.
 */
type NavTileProps = {
  /** Non-null when the target step may not be entered; used as tooltip copy. */
  blockedReason: string | null;
  onActivate: () => void;
  className: string;
  children: ReactNode;
};

const BLOCKED_CLASSES = "cursor-not-allowed opacity-60";

/** Compact mobile indicator (already a real button). */
const NavIndicatorButton = ({
  blockedReason,
  onActivate,
  className,
  children,
}: NavTileProps) => {
  const blocked = !!blockedReason;
  // aria-disabled rather than `disabled`: a disabled button receives no pointer
  // events, so the tooltip explaining *why* the tile is inert would never open.
  const button = (
    <button
      type="button"
      onClick={blocked ? undefined : onActivate}
      aria-disabled={blocked || undefined}
      className={`${className} ${blocked ? BLOCKED_CLASSES : ""}`}
    >
      {children}
    </button>
  );

  return blocked ? <Tooltip content={blockedReason}>{button}</Tooltip> : button;
};

/** Expanded-mobile / desktop summary card. */
const NavSection = ({
  blockedReason,
  onActivate,
  className,
  children,
}: NavTileProps) => {
  const blocked = !!blockedReason;
  const section = (
    <div
      className={`${className} ${blocked ? BLOCKED_CLASSES : ""}`}
      onClick={blocked ? undefined : onActivate}
      role="button"
      tabIndex={blocked ? -1 : 0}
      aria-disabled={blocked || undefined}
      onKeyDown={(e) => {
        if (!blocked && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onActivate();
        }
      }}
    >
      {children}
    </div>
  );

  return blocked ? <Tooltip content={blockedReason}>{section}</Tooltip> : section;
};

export default function SchedulerSummaryBar({ services, bundles }: SchedulerSummaryBarProps) {
  const {
    selectedPrimaryService,
    selectedBundle,
    selectedAddons,
    selectedServiceIds,
    containingServiceAreaArray,
    address,
    streetViewUrl,
    selectedDateTime,
    contact,
    additionalContacts,
    selectedSlotInspectorInfo,
    priceData,
    scheduleSettings,
    workingHoldExpiresAt,
    bookingStatus,
    setBookingStatus,
    isSendingQuote,
    setIsSendingQuote,
    companyStyles,
    draftQuoteId,
    draftQuoteSlug,
    workingHold,
    requiredInfoValues,
    equipmentQuantities,
    jwtToken,
    companyId,
    setDraftQuoteId,
    setDraftQuoteSlug,
    setWorkingHold,
    setWorkingHoldExpiresAt,
    appliedDiscountCode,
    appliedDiscountServiceIds,
    cancelDebounce,
  } = useScheduler();

  const { setActiveStep, activeStep } = useStepNavigation();
  const { canAccessStep, getBlockingFields } = useStepAccess();
  // Full-funnel rollup: services, property (incl. required info), slot, contact.
  const summaryValidation = useStepValidation(5);
  const tzDayjs = useTZDayjs();

  // When escape hatch applies (e.g. size-limit), block jump to step 3 and block Schedule Now / Send quote
  const escapeHatchReason = useMemo(
    () =>
      checkEscapeHatch({
        selectedServiceIds,
        services,
        containingServiceAreaArray,
        address,
        maxSqftOnline: scheduleSettings.maxSqftOnline,
      }),
    [
      selectedServiceIds,
      services,
      containingServiceAreaArray,
      address,
      scheduleSettings.maxSqftOnline,
    ],
  );
  const escapeHatchWouldApply = escapeHatchReason !== null;

  // Countdown timer for working hold
  const countdown = useCountdown(workingHoldExpiresAt);

  // Mobile expand/collapse state
  const [isExpanded, setIsExpanded] = useState(false);

  // Handle Send Quote - just set the state, loader component will handle the actual sending
  const handleSendQuote = () => {
    if (!draftQuoteId || !address || !priceData) {
      return; // Validation handled in SendQuoteLoader
    }

    // Cancel any pending debounced saves to prevent race condition
    cancelDebounce();

    // Set state to show loader - SendQuoteLoader will handle the actual sending
    setIsSendingQuote(true);
  };

  // Get price from context
  const totalPrice = priceData?.totalPrice || 0;

  // Calculate time range for slot
  const timeRange = useMemo(() => {
    if (!selectedDateTime) return null;
    const hours = jobDurationHoursForScheduling(priceData?.totalDuration ?? 0);
    const startTime = tzDayjs(selectedDateTime);
    const endTime = startTime.add(hours * 60, "minutes");
    return {
      start: startTime.format("h:mma"),
      end: endTime.format("h:mma"),
    };
  }, [selectedDateTime, priceData?.totalDuration, tzDayjs]);

  // Format duration range (e.g., "~3-4 hours")
  const durationRange = useMemo(() => {
    const durationInHours = priceData?.totalDuration ?? 0;
    const displayHours = jobDurationHoursForScheduling(durationInHours);

    const lower = Math.floor(displayHours);
    const upper = lower + 1;

    return `~${lower}-${upper} hours`;
  }, [priceData?.totalDuration]);

  // Get selected services list
  const selectedServices = useMemo(() => {
    const serviceList: Service[] = [];
    const serviceIdsSet = new Set<string>();

    // Add primary service
    if (selectedPrimaryService) {
      serviceIdsSet.add(selectedPrimaryService);
      const primary = findService(selectedPrimaryService, services);
      if (primary) serviceList.push(primary);
    }

    // Add addon services
    selectedAddons.forEach((addonId) => {
      if (!serviceIdsSet.has(addonId)) {
        serviceIdsSet.add(addonId);
        const addon = findService(addonId, services);
        if (addon) serviceList.push(addon);
      }
    });

    // Add services from selected bundle
    if (selectedBundle) {
      const bundle = bundles.find((b) => b._id === selectedBundle);
      bundle?.items?.forEach((item) => {
        if (item._serviceId && !serviceIdsSet.has(item._serviceId)) {
          serviceIdsSet.add(item._serviceId);
          const service = findService(item._serviceId, services);
          if (service) serviceList.push(service);
        }
      });
    }

    return serviceList;
  }, [selectedPrimaryService, selectedAddons, selectedBundle, bundles, services]);

  // Get primary service
  const primaryService = useMemo(() => {
    if (!selectedPrimaryService) return null;
    return findService(selectedPrimaryService, services) || null;
  }, [selectedPrimaryService, services]);

  // Get other services count
  const otherServicesCount = useMemo(() => {
    return selectedServices.length - (primaryService ? 1 : 0);
  }, [selectedServices.length, primaryService]);

  // Calculate property age
  const propertyAge = useMemo(() => {
    const year = address?.yearBuilt;
    return year ? new Date().getFullYear() - year : null;
  }, [address]);

  /**
   * Why a tile is inert, or null when the step may be entered. Two independent
   * reasons: the escape hatch (this property is not bookable online at all) and
   * unfinished earlier steps.
   */
  const stepBlockedReason = (step: number): string | null => {
    if (step === 3 && escapeHatchWouldApply) {
      return "Property exceeds max sq ft for online booking—contact for a custom quote";
    }
    if (!canAccessStep(step)) {
      const blockingFields = getBlockingFields(step);
      return blockingFields.length > 0
        ? `Finish this first: ${blockingFields.join(", ")}`
        : "Complete the previous steps first";
    }
    return null;
  };

  const goToStep = (step: number) => () => {
    if (stepBlockedReason(step)) return;
    setActiveStep(step);
  };

  // Hide summary bar when booking or sending quote is in progress
  // (those states show full-screen loaders/success screens)
  if (bookingStatus !== "idle" || isSendingQuote) {
    return null;
  }

  const sectionClasses = (isActive: boolean) =>
    `${SECTION_CLASSES} ${
      isActive ? "border-solid border-gray-300" : "border-dashed border-gray-300"
    }`;

  const indicatorClasses = (isActive: boolean) =>
    `flex flex-col items-center gap-1 rounded-lg p-2 transition-colors ${
      isActive ? "bg-gray-100" : "bg-gray-50 opacity-50"
    }`;

  return (
    <>
      <div
        data-summary-bar
        className="fixed bottom-0 left-0 right-0 z-30 border-t bg-white/80 backdrop-blur-sm"
      >
        <div className="mx-auto max-w-7xl px-4 py-3">
          {activeStep === 5 ? (
            // Action Buttons on Summary Step - disabled when the escape hatch applies
            // or when any earlier step is unfinished. Sending yourself a
            // quote stays available: that is lead capture, not a booked job.
            <div className="flex flex-col items-center gap-2">
              <Button
                size="lg"
                className={`min-w-[200px] font-bold uppercase ${companyStyles.secondaryButtonClassName}`}
                style={companyStyles.secondaryButtonStyles}
                onPress={() => setBookingStatus("loading")}
                isDisabled={
                  isSendingQuote || escapeHatchWouldApply || !summaryValidation.isValid
                }
              >
                Schedule Now
              </Button>
              {!summaryValidation.isValid && !escapeHatchWouldApply && (
                <p className="max-w-md text-center text-sm text-danger">
                  Before booking, please complete:{" "}
                  {summaryValidation.missingFields.join(", ")}
                </p>
              )}
              <p className="text-sm text-gray-600">
                Not ready to schedule?{" "}
                <button
                  onClick={handleSendQuote}
                  disabled={isSendingQuote || escapeHatchWouldApply}
                  className="cursor-pointer underline transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    color:
                      companyStyles.secondaryButtonStyles?.backgroundColor ||
                      companyStyles.companyColor ||
                      "#006FEE",
                  }}
                >
                  {isSendingQuote ? "sending..." : "Hold your slot and send yourself a quote"}
                </button>
              </p>
            </div>
          ) : (
            // Summary Blocks
            <>
              {/* Mobile Compact View */}
              <div className="sm:hidden">
                {!isExpanded ? (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-1 items-center justify-around gap-2">
                      {/* Services Indicator */}
                      <NavIndicatorButton
                        blockedReason={stepBlockedReason(1)}
                        onActivate={goToStep(1)}
                        className={indicatorClasses(!!primaryService)}
                      >
                        <div
                          className="flex h-10 w-10 items-center justify-center rounded-full"
                          style={{
                            backgroundColor: primaryService
                              ? companyStyles.companyColor
                                ? `${companyStyles.companyColor}1A`
                                : "rgba(0, 111, 238, 0.1)"
                              : "transparent",
                            color: primaryService
                              ? companyStyles.companyColor || "#006FEE"
                              : "#9CA3AF",
                            border: primaryService ? "none" : "2px dashed #D1D5DB",
                          }}
                        >
                          {primaryService ? (
                            <IconDisplay
                              iconName={primaryService.icon}
                              size={20}
                              defaultIcon="FaScrewdriverWrench"
                              className="text-lg"
                            />
                          ) : (
                            <FaScrewdriverWrench className="text-lg" />
                          )}
                        </div>
                        {primaryService && (
                          <span className="text-[10px] font-medium text-gray-600">
                            {scheduleSettings?.showPricingInOnlineScheduler !== false &&
                            address &&
                            totalPrice
                              ? `$${totalPrice?.toFixed(0)}`
                              : "Price TBD"}
                          </span>
                        )}
                      </NavIndicatorButton>

                      {/* People Indicator */}
                      <NavIndicatorButton
                        blockedReason={stepBlockedReason(4)}
                        onActivate={goToStep(4)}
                        className={indicatorClasses(!!contact)}
                      >
                        {contact ? (
                          <Avatar
                            src={contact.thumb}
                            size="sm"
                            className="h-10 w-10 shrink-0"
                            fallback={`${contact.firstName?.charAt(
                              0,
                            )}${contact.lastName?.charAt(0)}`}
                          />
                        ) : (
                          <div
                            className="flex h-10 w-10 items-center justify-center rounded-full"
                            style={{
                              backgroundColor: contact
                                ? companyStyles.companyColor
                                  ? `${companyStyles.companyColor}1A`
                                  : "rgba(0, 111, 238, 0.1)"
                                : "transparent",
                              color: contact ? companyStyles.companyColor || "#006FEE" : "#9CA3AF",
                              border: contact ? "none" : "2px dashed #D1D5DB",
                            }}
                          >
                            <FaUser className="text-lg" />
                          </div>
                        )}
                        {additionalContacts.length > 0 && (
                          <span className="text-[10px] font-medium text-gray-600">
                            +{additionalContacts.length}
                          </span>
                        )}
                      </NavIndicatorButton>

                      {/* Property Indicator */}
                      <NavIndicatorButton
                        blockedReason={stepBlockedReason(2)}
                        onActivate={goToStep(2)}
                        className={indicatorClasses(!!address)}
                      >
                        {address && streetViewUrl ? (
                          <img
                            src={streetViewUrl}
                            alt="Property"
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <div
                            className="flex h-10 w-10 items-center justify-center rounded-full"
                            style={{
                              backgroundColor: address
                                ? companyStyles.companyColor
                                  ? `${companyStyles.companyColor}1A`
                                  : "rgba(0, 111, 238, 0.1)"
                                : "transparent",
                              color: address ? companyStyles.companyColor || "#006FEE" : "#9CA3AF",
                              border: address ? "none" : "2px dashed #D1D5DB",
                            }}
                          >
                            <FaHouse className="text-lg" />
                          </div>
                        )}
                      </NavIndicatorButton>

                      {/* Slot Indicator - inert when the escape hatch applies or an
                          earlier step is unfinished */}
                      <NavIndicatorButton
                        blockedReason={stepBlockedReason(3)}
                        onActivate={goToStep(3)}
                        className={indicatorClasses(!!selectedDateTime)}
                      >
                        {scheduleSettings.showInspectorInOnlineScheduler &&
                        selectedSlotInspectorInfo ? (
                          <Avatar
                            src={selectedSlotInspectorInfo?.thumb || undefined}
                            size="sm"
                            className="h-10 w-10 shrink-0"
                            fallback={
                              selectedSlotInspectorInfo
                                ? `${selectedSlotInspectorInfo.firstName?.charAt(0) || ""}${
                                    selectedSlotInspectorInfo.lastName?.charAt(0) || ""
                                  }`
                                : "?"
                            }
                          />
                        ) : (
                          <div
                            className="flex h-10 w-10 items-center justify-center rounded-full"
                            style={{
                              backgroundColor: selectedDateTime
                                ? companyStyles.companyColor
                                  ? `${companyStyles.companyColor}1A`
                                  : "rgba(0, 111, 238, 0.1)"
                                : "transparent",
                              color: selectedDateTime
                                ? companyStyles.companyColor || "#006FEE"
                                : "#9CA3AF",
                              border: selectedDateTime ? "none" : "2px dashed #D1D5DB",
                            }}
                          >
                            <FaCalendarDay className="text-lg" />
                          </div>
                        )}
                        {selectedDateTime && (
                          <span className="text-[10px] font-medium text-gray-600">
                            {tzDayjs(selectedDateTime).format("MMM D")}
                          </span>
                        )}
                      </NavIndicatorButton>
                    </div>
                    <button
                      onClick={() => setIsExpanded(true)}
                      className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white transition-colors hover:bg-gray-50"
                    >
                      <FaChevronUp className="text-gray-600" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700">Summary</span>
                      <button
                        onClick={() => setIsExpanded(false)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white transition-colors hover:bg-gray-50"
                      >
                        <FaChevronDown className="text-gray-600" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      {/* Services Section */}
                      <NavSection
                        blockedReason={stepBlockedReason(1)}
                        onActivate={goToStep(1)}
                        className={sectionClasses(!!primaryService)}
                      >
                        {primaryService ? (
                          <div className="flex items-center gap-2">
                            <div
                              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                              style={{
                                backgroundColor: companyStyles.companyColor
                                  ? `${companyStyles.companyColor}1A`
                                  : "rgba(0, 111, 238, 0.1)",
                                color: companyStyles.companyColor || "#006FEE",
                              }}
                            >
                              <IconDisplay
                                iconName={primaryService.icon}
                                size={20}
                                defaultIcon="FaScrewdriverWrench"
                                className="text-lg"
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">{primaryService.name}</p>
                              <p className="text-xs text-gray-500">
                                {otherServicesCount > 0 &&
                                  `+ ${otherServicesCount} other service${
                                    otherServicesCount !== 1 ? "s" : ""
                                  } • `}
                                {scheduleSettings?.showPricingInOnlineScheduler !== false &&
                                address &&
                                totalPrice
                                  ? `$${totalPrice?.toFixed(2)}`
                                  : "Price TBD"}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-dashed border-gray-300 bg-gray-50">
                              <FaScrewdriverWrench className="text-lg text-gray-400" />
                            </div>
                            <div className="flex-1">
                              <div className="mb-1 h-3 w-24 rounded border border-dashed border-gray-300 bg-gray-50"></div>
                              <div className="mb-1 h-3 w-20 rounded border border-dashed border-gray-300 bg-gray-50"></div>
                              <p className="text-xs text-gray-400">No services selected</p>
                            </div>
                          </div>
                        )}
                      </NavSection>

                      {/* People Section */}
                      <NavSection
                        blockedReason={stepBlockedReason(4)}
                        onActivate={goToStep(4)}
                        className={sectionClasses(!!contact)}
                      >
                        {contact ? (
                          <div className="flex items-center gap-2">
                            <Avatar
                              src={contact.thumb}
                              size="sm"
                              className="h-8 w-8 shrink-0"
                              fallback={`${contact.firstName?.charAt(
                                0,
                              )}${contact.lastName?.charAt(0)}`}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">
                                {contact.firstName} {contact.lastName}
                              </p>
                              <p className="truncate text-xs text-gray-500">{contact.email}</p>
                              {additionalContacts.length > 0 && (
                                <p className="mt-0.5 text-xs text-gray-400">
                                  + {additionalContacts.length} other
                                  {additionalContacts.length !== 1 ? "s" : ""}
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full border-2 border-dashed border-gray-300 bg-gray-50"></div>
                            <div className="flex-1">
                              <div className="mb-1 h-3 w-20 rounded border border-dashed border-gray-300 bg-gray-50"></div>
                              <p className="text-xs text-gray-400">No contact added</p>
                            </div>
                          </div>
                        )}
                      </NavSection>

                      {/* Property Section */}
                      <NavSection
                        blockedReason={stepBlockedReason(2)}
                        onActivate={goToStep(2)}
                        className={sectionClasses(!!address)}
                      >
                        {address ? (
                          <div className="flex items-center gap-2">
                            {streetViewUrl && (
                              <img
                                src={streetViewUrl}
                                alt="Property"
                                className="h-10 w-10 rounded object-cover"
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">
                                {address.address || `${address.street}, ${address.city}`}
                              </p>
                              <p className="truncate text-xs text-gray-500">
                                {address.sqft && `${address.sqft.toLocaleString()} sqft`}
                                {propertyAge && ` • ${propertyAge} years`}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="h-10 w-10 rounded border border-dashed border-gray-300 bg-gray-50"></div>
                            <div className="flex-1">
                              <div className="mb-1 h-3 w-32 rounded border border-dashed border-gray-300 bg-gray-50"></div>
                              <p className="text-xs text-gray-400">No property selected</p>
                            </div>
                          </div>
                        )}
                      </NavSection>

                      {/* Slot Section */}
                      <NavSection
                        blockedReason={stepBlockedReason(3)}
                        onActivate={goToStep(3)}
                        className={sectionClasses(!!selectedDateTime)}
                      >
                        {selectedDateTime ? (
                          <div className="flex items-center gap-2">
                            {scheduleSettings.showInspectorInOnlineScheduler ? (
                              <Avatar
                                src={selectedSlotInspectorInfo?.thumb || undefined}
                                size="sm"
                                className="h-8 w-8 shrink-0"
                                fallback={
                                  selectedSlotInspectorInfo
                                    ? `${selectedSlotInspectorInfo.firstName?.charAt(0) || ""}${
                                        selectedSlotInspectorInfo.lastName?.charAt(0) || ""
                                      }`
                                    : "?"
                                }
                              />
                            ) : (
                              <div
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                                style={{
                                  backgroundColor: companyStyles.companyColor
                                    ? `${companyStyles.companyColor}1A`
                                    : "rgba(0, 111, 238, 0.1)",
                                  color: companyStyles.companyColor || "#006FEE",
                                }}
                              >
                                <FaCalendarDay className="text-lg" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">
                                {tzDayjs(selectedDateTime).format("ddd, MMM D")}
                              </p>
                              <p className="text-xs text-gray-500">
                                {tzDayjs(selectedDateTime).format("h:mma")}
                                {scheduleSettings.showDurationInOnlineScheduler &&
                                  durationRange && (
                                    <span className="ml-1">({durationRange})</span>
                                  )}
                                {scheduleSettings.showInspectorInOnlineScheduler &&
                                  selectedSlotInspectorInfo && (
                                    <span className="ml-1">
                                      • {selectedSlotInspectorInfo.firstName}{" "}
                                      {selectedSlotInspectorInfo.lastName}
                                    </span>
                                  )}
                              </p>
                              {!countdown.isExpired && (
                                <p className="text-xs text-warning-700">
                                  Hold expires in {countdown.minutes}:
                                  {countdown.seconds.toString().padStart(2, "0")}
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full border-2 border-dashed border-gray-300 bg-gray-50"></div>
                            <div className="flex-1">
                              <div className="mb-1 h-3 w-24 rounded border border-dashed border-gray-300 bg-gray-50"></div>
                              <p className="text-xs text-gray-400">No slot selected</p>
                            </div>
                          </div>
                        )}
                      </NavSection>
                    </div>
                  </div>
                )}
              </div>

              {/* Desktop View */}
              <div className="hidden grid-cols-4 gap-4 sm:grid sm:items-center">
                {/* Services Section */}
                <NavSection
                  blockedReason={stepBlockedReason(1)}
                  onActivate={goToStep(1)}
                  className={sectionClasses(!!primaryService)}
                >
                  {primaryService ? (
                    <div className="flex items-center gap-2">
                      <div
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                        style={{
                          backgroundColor: companyStyles.companyColor
                            ? `${companyStyles.companyColor}1A`
                            : "rgba(0, 111, 238, 0.1)",
                          color: companyStyles.companyColor || "#006FEE",
                        }}
                      >
                        <IconDisplay
                          iconName={primaryService.icon}
                          size={20}
                          defaultIcon="FaScrewdriverWrench"
                          className="text-lg"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{primaryService.name}</p>
                        <p className="text-xs text-gray-500">
                          {otherServicesCount > 0 &&
                            `+ ${otherServicesCount} other service${
                              otherServicesCount !== 1 ? "s" : ""
                            } • `}
                          {scheduleSettings?.showPricingInOnlineScheduler !== false &&
                          address &&
                          totalPrice
                            ? `$${totalPrice?.toFixed(2)}`
                            : "Price TBD"}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-dashed border-gray-300 bg-gray-50">
                        <FaScrewdriverWrench className="text-lg text-gray-400" />
                      </div>
                      <div className="flex-1">
                        <div className="mb-1 h-3 w-24 rounded border border-dashed border-gray-300 bg-gray-50"></div>
                        <div className="mb-1 h-3 w-20 rounded border border-dashed border-gray-300 bg-gray-50"></div>
                        <p className="text-xs text-gray-400">No services selected</p>
                      </div>
                    </div>
                  )}
                </NavSection>

                {/* People Section */}
                <NavSection
                  blockedReason={stepBlockedReason(4)}
                  onActivate={goToStep(4)}
                  className={sectionClasses(!!contact)}
                >
                  {contact ? (
                    <div className="flex items-center gap-2">
                      <Avatar
                        src={contact.thumb}
                        size="sm"
                        className="h-8 w-8 shrink-0"
                        fallback={`${contact.firstName?.charAt(0)}${contact.lastName?.charAt(0)}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {contact.firstName} {contact.lastName}
                        </p>
                        <p className="truncate text-xs text-gray-500">{contact.email}</p>
                        {additionalContacts.length > 0 && (
                          <p className="mt-0.5 text-xs text-gray-400">
                            + {additionalContacts.length} other
                            {additionalContacts.length !== 1 ? "s" : ""}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full border-2 border-dashed border-gray-300 bg-gray-50"></div>
                      <div className="flex-1">
                        <div className="mb-1 h-3 w-20 rounded border border-dashed border-gray-300 bg-gray-50"></div>
                        <p className="text-xs text-gray-400">No contact added</p>
                      </div>
                    </div>
                  )}
                </NavSection>

                {/* Property Section */}
                <NavSection
                  blockedReason={stepBlockedReason(2)}
                  onActivate={goToStep(2)}
                  className={sectionClasses(!!address)}
                >
                  {address ? (
                    <div className="flex items-center gap-2">
                      {streetViewUrl && (
                        <img
                          src={streetViewUrl}
                          alt="Property"
                          className="h-10 w-10 rounded object-cover"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {address.address || `${address.street}, ${address.city}`}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                          {address.sqft && `${address.sqft.toLocaleString()} sqft`}
                          {propertyAge && ` • ${propertyAge} years`}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="h-10 w-10 rounded border border-dashed border-gray-300 bg-gray-50"></div>
                      <div className="flex-1">
                        <div className="mb-1 h-3 w-32 rounded border border-dashed border-gray-300 bg-gray-50"></div>
                        <p className="text-xs text-gray-400">No property selected</p>
                      </div>
                    </div>
                  )}
                </NavSection>

                {/* Slot Section */}
                <NavSection
                  blockedReason={stepBlockedReason(3)}
                  onActivate={goToStep(3)}
                  className={sectionClasses(!!selectedDateTime)}
                >
                  {selectedDateTime ? (
                    <div className="flex items-center gap-2">
                      {scheduleSettings.showInspectorInOnlineScheduler ? (
                        <Avatar
                          src={selectedSlotInspectorInfo?.thumb || undefined}
                          size="sm"
                          className="h-8 w-8 shrink-0"
                          fallback={
                            selectedSlotInspectorInfo
                              ? `${selectedSlotInspectorInfo.firstName?.charAt(0) || ""}${
                                  selectedSlotInspectorInfo.lastName?.charAt(0) || ""
                                }`
                              : "?"
                          }
                        />
                      ) : (
                        <div
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                          style={{
                            backgroundColor: companyStyles.companyColor
                              ? `${companyStyles.companyColor}1A`
                              : "rgba(0, 111, 238, 0.1)",
                            color: companyStyles.companyColor || "#006FEE",
                          }}
                        >
                          <FaCalendarDay className="text-lg" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {tzDayjs(selectedDateTime).format("ddd, MMM D")}
                        </p>
                        <p className="text-xs text-gray-500">
                          {tzDayjs(selectedDateTime).format("h:mma")}
                          {scheduleSettings.showDurationInOnlineScheduler &&
                            durationRange && (
                              <span className="ml-1">({durationRange})</span>
                            )}
                          {scheduleSettings.showInspectorInOnlineScheduler &&
                            selectedSlotInspectorInfo && (
                              <span className="ml-1">
                                • {selectedSlotInspectorInfo.firstName}{" "}
                                {selectedSlotInspectorInfo.lastName}
                              </span>
                            )}
                        </p>
                        {!countdown.isExpired && (
                          <p className="text-xs text-warning-700">
                            Hold expires in {countdown.minutes}:
                            {countdown.seconds.toString().padStart(2, "0")}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full border-2 border-dashed border-gray-300 bg-gray-50"></div>
                      <div className="flex-1">
                        <div className="mb-1 h-3 w-24 rounded border border-dashed border-gray-300 bg-gray-50"></div>
                        <p className="text-xs text-gray-400">No slot selected</p>
                      </div>
                    </div>
                  )}
                </NavSection>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Spacer to prevent content from being hidden behind the sticky bar */}
      <div
        className="sm:h-[88px]"
        style={{
          height: isExpanded ? "320px" : "88px",
        }}
      />
    </>
  );
}
